import { useState, type ReactNode } from "react";
import { Box, ButtonBase, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import Accessible from "@mui/icons-material/Accessible";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import CallOutlined from "@mui/icons-material/CallOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import ChairOutlined from "@mui/icons-material/ChairOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ChecklistOutlined from "@mui/icons-material/ChecklistOutlined";
import DirectionsOutlined from "@mui/icons-material/DirectionsOutlined";
import DragIndicator from "@mui/icons-material/DragIndicator";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import FacebookOutlined from "@mui/icons-material/FacebookOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import MapOutlined from "@mui/icons-material/MapOutlined";
import Instagram from "@mui/icons-material/Instagram";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import LocalParkingOutlined from "@mui/icons-material/LocalParkingOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import MeetingRoomOutlined from "@mui/icons-material/MeetingRoomOutlined";
import NotesOutlined from "@mui/icons-material/NotesOutlined";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import Star from "@mui/icons-material/Star";
import StarOutline from "@mui/icons-material/StarOutline";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import SwapVert from "@mui/icons-material/SwapVert";
import WifiOutlined from "@mui/icons-material/WifiOutlined";
import { FIELD_ICONS, toneColor, type Audience, type VisibilityField } from "../../venues/websiteVisibility";

export interface PageSection {
  key: string;
  label: string;
}

/** Sections that run full width on the website; the contact card sits beside everything before the first one. */
const WIDE = new Set(["streams", "reviews"]);

const SECTION_ICONS: Record<string, ReactNode> = {
  about: <NotesOutlined />,
  amenities: <ChecklistOutlined />,
  layouts: <ChairOutlined />,
  rooms: <MeetingRoomOutlined />,
  streams: <PlayCircleOutline />,
  reviews: <StarOutline />,
};

/** What the phone shows: a real venue (publishing panel) or the sample (settings page). */
export interface PreviewVenue {
  name: string;
  type: string;
  area: string;
  address: string;
  capacity: number | null;
  coverUrl: string | null;
  photoCount: number;
  description: string;
  amenities: string[];
  layouts: { name: string; capacity: number | null }[];
  rooms: { name: string; capacity: number | null }[];
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  rating: { average: number; count: number } | null;
}

export const SAMPLE_VENUE: PreviewVenue = {
  name: "Riverside Chapel",
  type: "Chapel",
  area: "Avondale, Harare",
  address: "12 River Rd",
  capacity: 250,
  coverUrl: null,
  photoCount: 8,
  description: "",
  amenities: ["Parking", "Wheelchair access", "Seating"],
  layouts: [{ name: "Theatre", capacity: 250 }, { name: "Banquet", capacity: 120 }],
  rooms: [{ name: "Side chapel", capacity: 40 }, { name: "Family room", capacity: 15 }],
  contactName: "Jane Moyo",
  contactPhone: "+263 77 123 4567",
  contactEmail: "events@riverside.co.zw",
  rating: { average: 4.8, count: 12 },
};

function amenityIcon(name: string) {
  const text = name.toLowerCase();
  if (text.includes("park")) return <LocalParkingOutlined />;
  if (text.includes("wi-fi") || text.includes("wifi") || text.includes("internet")) return <WifiOutlined />;
  if (text.includes("wheel") || text.includes("access")) return <Accessible />;
  if (text.includes("seat") || text.includes("chair")) return <ChairOutlined />;
  return <CheckCircle />;
}
const RANK: Record<Audience, number> = { hidden: 0, members: 1, everyone: 2 };

/** Where an extra field shows on the website; anything unknown is an amenity, as on the website. */
const showsIn = (field: VisibilityField) =>
  ["layouts", "visiting", "location", "streams"].includes(field.shows_in ?? "") ? (field.shows_in as string) : "amenities";

type Viewer = "visitor" | "member";

export type Mode = Viewer | "arrange";

const MODES: { value: Mode; label: string; icon: ReactNode }[] = [
  { value: "visitor", label: "Visitor", icon: <PublicOutlined /> },
  { value: "member", label: "Member", icon: <LockOutlined /> },
  { value: "arrange", label: "Arrange", icon: <SwapVert /> },
];

/**
 * A phone-sized venue page drawn with sample content, showing exactly which
 * sections and details a visitor or a signed-in member gets, in the arranged
 * order. In Arrange mode the sections can be dragged (or moved with the arrows).
 */
export default function WebsitePreview({
  fields,
  audienceOf,
  sections,
  order,
  onOrderChange,
  unlocks,
  venue = SAMPLE_VENUE,
  modes = ["visitor", "member", "arrange"],
}: {
  fields: VisibilityField[];
  audienceOf: (field: VisibilityField) => Audience;
  sections: PageSection[];
  order: string[];
  /** Missing when the viewer can't edit, so Arrange is read-only. */
  onOrderChange?: (order: string[]) => void;
  /** What signing in unlocks, for the sign-in card. */
  unlocks: string[];
  /** The venue drawn in the phone; the sample one unless given. */
  venue?: PreviewVenue;
  /** Which choices the switch offers; the publishing panel leaves out Arrange. */
  modes?: Mode[];
}) {
  const [mode, setMode] = useState<Mode>("visitor");
  const arranging = mode === "arrange";
  const viewer: Viewer = mode === "member" ? "member" : "visitor";
  const byKey = Object.fromEntries(fields.map((field) => [field.key, field]));
  const audience = (key: string): Audience => (byKey[key] ? audienceOf(byKey[key]) : "hidden");
  const sees = (key: string) => audience(key) === "everyone" || (viewer === "member" && audience(key) === "members");

  /** Added extra fields that join a given part of the page (backend: PublicField.shows_in). */
  const placed = (where: string) => fields.filter((field) => field.optional && showsIn(field) === where).map((field) => field.key);

  /** Who gets a whole section: the most open of the fields that make it up. */
  const sectionAudience = (key: string): Audience => {
    const keys =
      key === "about" ? ["description"]
      : key === "amenities" ? ["amenities", ...placed("amenities")]
      : key === "layouts" ? ["layouts", ...placed("layouts")]
      : [key];
    return keys.map(audience).reduce<Audience>((best, next) => (RANK[next] > RANK[best] ? next : best), "hidden");
  };
  const label = (key: string) => sections.find((section) => section.key === key)?.label ?? key;

  return (
    <Stack spacing={2} alignItems="center">
      {/* One switch: look at the page as a visitor or a member, or arrange it. Only one is ever on. */}
      <Box
        role="radiogroup"
        aria-label="Preview mode"
        sx={{ display: "inline-grid", gridTemplateColumns: `repeat(${modes.length}, 1fr)`, gap: 0.25, p: 0.375, borderRadius: 2.5, bgcolor: "#F1F3F7" }}
      >
        {MODES.filter((option) => modes.includes(option.value)).map((option) => {
          const selected = mode === option.value;
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={selected}
              aria-label={option.label}
              onClick={() => setMode(option.value)}
              sx={{
                minWidth: 96,
                height: 34,
                px: 1.5,
                gap: 0.75,
                borderRadius: 2,
                fontSize: 13,
                fontWeight: selected ? 700 : 600,
                color: selected ? "text.primary" : "text.secondary",
                bgcolor: selected ? "background.paper" : "transparent",
                boxShadow: selected ? "0 1px 2px rgba(16,24,40,.08), 0 1px 3px rgba(16,24,40,.10)" : "none",
                transition: "background-color .15s, color .15s, box-shadow .15s",
                "& svg": { fontSize: 17, color: selected ? "#2563EB" : "inherit" },
                "&:hover": selected ? {} : { color: "text.primary" },
              }}
            >
              {option.icon}
              {option.label}
            </ButtonBase>
          );
        })}
      </Box>

      <Phone>
        {arranging ? (
          <ArrangeList order={order} label={label} sectionAudience={sectionAudience} onOrderChange={onOrderChange} />
        ) : (
          <PagePreview
            order={order}
            viewer={viewer}
            sees={sees}
            fields={fields}
            sectionAudience={sectionAudience}
            label={label}
            unlocks={viewer === "visitor" ? unlocks : []}
            venue={venue}
          />
        )}
      </Phone>
    </Stack>
  );
}

function Phone({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ width: "100%", maxWidth: 330, p: 1.25, borderRadius: "42px", bgcolor: "#111827", boxShadow: "0 24px 60px rgba(17,24,39,.28), inset 0 0 0 2px #2b3445" }}>
      <Box sx={{ position: "relative", borderRadius: "33px", overflow: "hidden", bgcolor: "#fff", height: 640 }}>
        <Box sx={{ position: "absolute", zIndex: 2, top: 9, left: "50%", transform: "translateX(-50%)", width: 84, height: 22, borderRadius: 999, bgcolor: "#111827" }} />
        <Box sx={{ height: "100%", overflowY: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" }, color: "#1f2937" }}>{children}</Box>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ preview */

const ACCENT = "#7a4a33";
const SOFT = "#f5f1ee";

function PagePreview({
  order,
  viewer,
  sees,
  fields,
  sectionAudience,
  label,
  unlocks,
  venue,
}: {
  order: string[];
  viewer: Viewer;
  sees: (key: string) => boolean;
  fields: VisibilityField[];
  sectionAudience: (key: string) => Audience;
  label: (key: string) => string;
  unlocks: string[];
  venue: PreviewVenue;
}) {
  const theme = useTheme();
  const membersTone = toneColor(theme, "members");
  const visible = (key: string) => sectionAudience(key) === "everyone" || (viewer === "member" && sectionAudience(key) === "members");
  // Like the website, a section with nothing to show isn't there at all.
  const extras = (where: string) => fields.some((field) => field.optional && showsIn(field) === where);
  const hasContent: Record<string, boolean> = {
    about: venue === SAMPLE_VENUE || Boolean(venue.description.trim()),
    amenities: venue.amenities.length > 0 || extras("amenities"),
    layouts: venue.layouts.length > 0 || extras("layouts"),
    rooms: venue.rooms.length > 0,
  };
  const shown = order.filter((key) => sectionAudience(key) !== "hidden" && hasContent[key] !== false);
  const firstWide = shown.findIndex((key) => WIDE.has(key));
  const beside = firstWide === -1 ? shown : shown.slice(0, firstWide);
  const below = firstWide === -1 ? [] : shown.slice(firstWide);

  const section = (key: string) =>
    visible(key) ? (
      <MiniSection key={key} title={label(key)}>
        <SectionBody sectionKey={key} sees={sees} fields={fields} venue={venue} />
      </MiniSection>
    ) : (
      // Visitors don't get this section; drawn faintly so it's clear what signing in adds.
      <Box key={key} sx={{ mx: 2, my: 1.25, px: 1.5, py: 1.1, borderRadius: 2, border: "1px dashed", borderColor: alpha(membersTone, 0.45), display: "flex", alignItems: "center", gap: 1, color: membersTone }}>
        <LockOutlined sx={{ fontSize: 15 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1 }}>{label(key)}</Typography>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.8 }}>Members</Typography>
      </Box>
    );

  return (
    <Box sx={{ pb: 3 }}>
      {/* Cover and gallery */}
      <Box sx={{ position: "relative", height: 178, background: venue.coverUrl ? `center / cover no-repeat url("${venue.coverUrl.replace(/"/g, "%22")}")` : "linear-gradient(160deg, #8a5a43 0%, #c79a7a 55%, #e9d3c0 100%)" }}>
        {!venue.coverUrl && (
          <>
            <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 70% 30%, rgba(255,255,255,.35), transparent 45%)" }} />
            <Box sx={{ position: "absolute", left: 18, bottom: -1, width: 120, height: 70, borderRadius: "60px 60px 0 0", bgcolor: "rgba(255,255,255,.22)" }} />
          </>
        )}
        {sees("gallery") && venue.photoCount > 1 && (
          <Box sx={{ position: "absolute", right: 10, bottom: 10, px: 1, py: 0.25, borderRadius: 999, bgcolor: "rgba(17,24,39,.65)", color: "#fff", fontSize: 10.5, fontWeight: 700 }}>1 / {venue.photoCount}</Box>
        )}
      </Box>

      {/* Name and facts */}
      <Box sx={{ px: 2, pt: 1.75 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: ACCENT, textTransform: "uppercase" }}>{venue.type}</Typography>
        <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2, fontFamily: "Georgia, serif" }}>{venue.name}</Typography>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 0.5, color: "#6b7280", fontSize: 11.5 }}>
          <Stack direction="row" spacing={0.25} alignItems="center"><LocationOnOutlined sx={{ fontSize: 13 }} /><span>{venue.area}{fields.some((field) => showsIn(field) === "location" && field.optional && sees(field.key)) ? ", Zimbabwe" : ""}</span></Stack>
          {sees("reviews") && venue.rating && <Stack direction="row" spacing={0.25} alignItems="center"><Star sx={{ fontSize: 13, color: "#f59e0b" }} /><b style={{ color: "#1f2937" }}>{venue.rating.average.toFixed(1)}</b><span>({venue.rating.count})</span></Stack>}
        </Stack>
        <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px", borderRadius: 2, overflow: "hidden", bgcolor: "#e5e7eb", border: "1px solid #e5e7eb" }}>
          {[
            sees("capacity") && venue.capacity && { icon: <GroupsOutlined />, k: "Capacity", v: `Up to ${venue.capacity}` },
            { icon: <StorefrontOutlined />, k: "Type", v: venue.type },
            { icon: <LocationOnOutlined />, k: sees("street_address") && venue.address ? "Address" : "Area", v: sees("street_address") && venue.address ? venue.address : venue.area },
            sees("reviews") && { icon: <Star />, k: "Rating", v: venue.rating ? `${venue.rating.average.toFixed(1)} · ${venue.rating.count}` : "No reviews yet" },
          ].filter(Boolean).map((fact) => {
            const item = fact as { icon: ReactNode; k: string; v: string };
            return (
              <Stack key={item.k} direction="row" spacing={0.75} alignItems="center" sx={{ bgcolor: "#fff", p: 1, "& svg": { fontSize: 15, color: ACCENT } }}>
                {item.icon}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, color: "#9ca3af", textTransform: "uppercase" }}>{item.k}</Typography>
                  <Typography noWrap sx={{ fontSize: 11.5, fontWeight: 700 }}>{item.v}</Typography>
                </Box>
              </Stack>
            );
          })}
        </Box>
      </Box>

      {beside.map(section)}
      <ContactCard sees={sees} fields={fields} venue={venue} />
      {unlocks.length > 0 && (
        <Box sx={{ mx: 2, mt: 1.5, p: 1.5, borderRadius: 2.5, background: `linear-gradient(135deg, ${SOFT}, #fff)`, border: "1px solid #eadfd8" }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: alpha(ACCENT, 0.14), display: "grid", placeItems: "center" }}>
              <LockOutlined sx={{ fontSize: 14, color: ACCENT }} />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>See the full venue details</Typography>
          </Stack>
          <Stack spacing={0.4} sx={{ mt: 1 }}>
            {unlocks.slice(0, 5).map((item) => (
              <Stack key={item} direction="row" spacing={0.6} alignItems="center" sx={{ fontSize: 11, color: "#6b7280" }}>
                <CheckCircle sx={{ fontSize: 12, color: ACCENT }} />
                <span>{item}</span>
              </Stack>
            ))}
            {unlocks.length > 5 && <Typography sx={{ fontSize: 11, color: "#9ca3af", pl: 2.25 }}>and {unlocks.length - 5} more</Typography>}
          </Stack>
          <Box sx={{ mt: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: ACCENT, color: "#fff", textAlign: "center", fontSize: 12, fontWeight: 700 }}>Sign in</Box>
        </Box>
      )}
      {below.map(section)}
    </Box>
  );
}

function MiniSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mx: 2, pt: 2, mt: 1.5, borderTop: "1px solid #eef0f3" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, fontFamily: "Georgia, serif", mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

const Line = ({ width }: { width: string }) => <Box sx={{ height: 6, width, borderRadius: 3, bgcolor: "#e5e7eb", mb: 0.75 }} />;

function Tile({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ px: 0.9, py: 0.75, borderRadius: 1.5, bgcolor: SOFT, fontSize: 10.5, fontWeight: 600, "& svg": { fontSize: 14, color: ACCENT } }}>
      {icon}
      <span>{text}</span>
    </Stack>
  );
}

function SectionBody({ sectionKey, sees, fields, venue }: { sectionKey: string; sees: (key: string) => boolean; fields: VisibilityField[]; venue: PreviewVenue }) {
  const largest = Math.max(1, ...venue.layouts.map((layout) => layout.capacity ?? 0), venue.capacity ?? 0);
  const extrasIn = (where: string) => fields.filter((field) => field.optional && showsIn(field) === where && sees(field.key));
  switch (sectionKey) {
    case "about":
      return venue.description.trim() ? (
        <Typography sx={{ fontSize: 11.5, color: "#4b5563", lineHeight: 1.5, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 4, overflow: "hidden", whiteSpace: "pre-line" }}>
          {venue.description}
        </Typography>
      ) : (
        <Box><Line width="100%" /><Line width="94%" /><Line width="70%" /></Box>
      );
    case "amenities":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
          {sees("amenities") && venue.amenities.slice(0, 6).map((amenity) => (
            <Tile key={amenity} icon={amenityIcon(amenity)} text={amenity} />
          ))}
          {/* Added extra fields with no other home read as amenities, e.g. "Power supply". */}
          {extrasIn("amenities").map((field) => (
            <Tile key={field.key} icon={FIELD_ICONS[field.key]} text={field.label} />
          ))}
        </Box>
      );
    case "layouts":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
          {sees("layouts") && venue.layouts.slice(0, 4).map((layout) => (
            <Box key={layout.name} sx={{ p: 1, borderRadius: 1.5, border: "1px solid #eef0f3" }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "capitalize" }} noWrap>{layout.name}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{layout.capacity ?? "—"}<span style={{ fontSize: 9.5, color: "#9ca3af", fontWeight: 500 }}> guests</span></Typography>
              <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: "#f1f3f5" }}><Box sx={{ height: 4, width: `${Math.min(100, ((layout.capacity ?? 0) / largest) * 100)}%`, borderRadius: 2, bgcolor: alpha(ACCENT, 0.7) }} /></Box>
            </Box>
          ))}
          {/* Floor plans: a button under the layouts that opens them. */}
          {extrasIn("layouts").map((field) => (
            <Stack key={field.key} direction="row" spacing={0.6} alignItems="center" sx={{ gridColumn: "1 / -1", justifySelf: "start", px: 1.25, py: 0.6, borderRadius: 1.5, border: "1px solid #e5e7eb", fontSize: 11, fontWeight: 700, "& svg": { fontSize: 14, color: ACCENT } }}>
              <MapOutlined />
              <span>View {field.label.replace(/s$/i, "").toLowerCase()}</span>
            </Stack>
          ))}
        </Box>
      );
    case "rooms":
      return (
        <Stack spacing={0.75}>
          {venue.rooms.slice(0, 4).map((room) => (
            <Stack key={room.name} direction="row" spacing={1} alignItems="center" sx={{ p: 0.9, borderRadius: 1.5, border: "1px solid #eef0f3", "& svg": { fontSize: 15, color: ACCENT } }}>
              <MeetingRoomOutlined />
              <Typography sx={{ fontSize: 11, fontWeight: 700, flex: 1 }} noWrap>{room.name}</Typography>
              {room.capacity && <Typography sx={{ fontSize: 10.5, color: "#6b7280" }}>{room.capacity} guests</Typography>}
            </Stack>
          ))}
        </Stack>
      );
    case "streams":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
          {extrasIn("streams").map((field) => (
            <Typography key={field.key} sx={{ gridColumn: "1 / -1", fontSize: 10.5, color: "#6b7280", mt: -0.5 }}>
              {field.key === "number_of_cameras" ? "Filmed with up to 3 camera angles." : field.label}
            </Typography>
          ))}
          {[0, 1].map((index) => (
            <Box key={index} sx={{ borderRadius: 1.5, overflow: "hidden", border: "1px solid #eef0f3" }}>
              <Box sx={{ height: 52, background: index ? "linear-gradient(135deg,#d6c3b6,#efe5dd)" : "linear-gradient(135deg,#b99a86,#e8d9cd)", display: "grid", placeItems: "center" }}>
                <PlayArrowRounded sx={{ fontSize: 20, color: "#fff" }} />
              </Box>
              <Box sx={{ p: 0.75 }}><Line width="85%" /><Line width="50%" /></Box>
            </Box>
          ))}
        </Box>
      );
    case "reviews":
      return (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{venue.rating ? venue.rating.average.toFixed(1) : "—"}</Typography>
            <Stack direction="row">{[0, 1, 2, 3, 4].map((index) => <Star key={index} sx={{ fontSize: 14, color: venue.rating && index < Math.round(venue.rating.average) ? "#f59e0b" : "#e5e7eb" }} />)}</Stack>
            <Typography sx={{ fontSize: 10.5, color: "#6b7280" }}>{venue.rating ? `${venue.rating.count} ${venue.rating.count === 1 ? "review" : "reviews"}` : "No reviews yet"}</Typography>
          </Stack>
          <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, border: "1px solid #eef0f3" }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, mb: 0.5 }}>Tendai M.</Typography>
            <Line width="95%" /><Line width="60%" />
          </Box>
        </Box>
      );
    default:
      return null;
  }
}

/** The venue's contact card, with only the parts this viewer gets. */
function ContactCard({ sees, fields, venue }: { sees: (key: string) => boolean; fields: VisibilityField[]; venue: PreviewVenue }) {
  const notes = fields.filter((field) => field.optional && showsIn(field) === "visiting" && sees(field.key));
  const person = sees("contact_name") || sees("contact_photo");
  const lines = [
    sees("contact_phone") && { key: "phone", icon: <CallOutlined />, text: venue.contactPhone || "Not provided" },
    sees("contact_email") && { key: "email", icon: <EmailOutlined />, text: venue.contactEmail || "Not provided" },
  ].filter(Boolean) as { key: string; icon: ReactNode; text: string }[];
  const socials = [["website", <LanguageOutlined key="w" />], ["facebook", <FacebookOutlined key="f" />], ["instagram", <Instagram key="i" />]].filter(([key]) => sees(key as string));
  const directions = sees("map");
  const address = sees("street_address");
  if (!person && !lines.length && !socials.length && !directions && !address && !notes.length) return null;

  return (
    <Box sx={{ mx: 2, mt: 2, p: 1.5, borderRadius: 2.5, border: "1px solid #eef0f3", boxShadow: "0 2px 8px rgba(17,24,39,.05)" }}>
      {/* Like the website: phone or email alone still get the header, named after the venue. */}
      {person || lines.length > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, background: sees("contact_photo") ? "linear-gradient(135deg,#c9a58c,#8a5a43)" : "linear-gradient(160deg,#8a5a43,#e9d3c0)" }} />
          <Box>
            <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, color: "#9ca3af" }}>YOUR VENUE CONTACT</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{sees("contact_name") && venue.contactName ? venue.contactName : venue.name}</Typography>
          </Box>
        </Stack>
      ) : address ? (
        <Box>
          <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, color: "#9ca3af" }}>VISITING</Typography>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>{[venue.address, venue.area].filter(Boolean).join(", ")}</Typography>
        </Box>
      ) : null}
      {lines.length > 0 && (
        <Box sx={{ mt: 1.25, borderRadius: 1.5, border: "1px solid #eef0f3" }}>
          {lines.map((line, index) => (
            <Stack key={line.key} direction="row" spacing={0.75} alignItems="center" sx={{ px: 1, py: 0.75, borderTop: index ? "1px solid #eef0f3" : 0, fontSize: 10.5, fontWeight: 600, "& svg": { fontSize: 14, color: ACCENT } }}>
              {line.icon}
              <span>{line.text}</span>
            </Stack>
          ))}
        </Box>
      )}
      {notes.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.25, p: 1, borderRadius: 1.5, bgcolor: SOFT }}>
          {notes.map((note) => (
            <Box key={note.key}>
              <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, color: "#9ca3af", textTransform: "uppercase" }}>{note.label}</Typography>
              <Line width={note.key === "time_zone" ? "45%" : "85%"} />
            </Box>
          ))}
        </Stack>
      )}
      {directions && (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mt: 1, py: 0.6, borderRadius: 1.5, border: "1px solid #e5e7eb", fontSize: 11, fontWeight: 700 }}>
          <DirectionsOutlined sx={{ fontSize: 14 }} />
          <span>Get directions</span>
        </Stack>
      )}
      {socials.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
          {socials.map(([key, icon]) => (
            <Box key={key as string} sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: SOFT, display: "grid", placeItems: "center", "& svg": { fontSize: 13, color: ACCENT } }}>{icon}</Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ arrange */

function ArrangeList({
  order,
  label,
  sectionAudience,
  onOrderChange,
}: {
  order: string[];
  label: (key: string) => string;
  sectionAudience: (key: string) => Audience;
  onOrderChange?: (order: string[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const move = (from: number, to: number) => onOrderChange?.(arrayMove(order, from, to));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) move(order.indexOf(String(active.id)), order.indexOf(String(over.id)));
  };
  const firstWide = order.findIndex((key) => WIDE.has(key));

  return (
    <Box sx={{ px: 1.5, pt: 5, pb: 3, bgcolor: "#f7f7fa", minHeight: "100%" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, px: 0.5 }}>Arrange the page</Typography>
      <Typography sx={{ fontSize: 11.5, color: "#6b7280", px: 0.5, mb: 1.5 }}>
        {onOrderChange ? "Drag sections, or use the arrows." : "Only Admins can change the order."}
      </Typography>

      <FixedRow icon={<StorefrontOutlined />} text="Name, photos and key facts" />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {order.map((key, index) => (
              <Box key={key}>
                {index === firstWide && <FixedRow icon={<CallOutlined />} text="Contact card" />}
                <SortableRow
                  id={key}
                  label={label(key)}
                  icon={SECTION_ICONS[key]}
                  audience={sectionAudience(key)}
                  disabled={!onOrderChange}
                  onUp={index > 0 ? () => move(index, index - 1) : undefined}
                  onDown={index < order.length - 1 ? () => move(index, index + 1) : undefined}
                />
              </Box>
            ))}
            {firstWide === -1 && <FixedRow icon={<CallOutlined />} text="Contact card" />}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  );
}

function FixedRow({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Tooltip title="Stays in place" placement="left">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.25, py: 1, my: 0.75, borderRadius: 2, border: "1px dashed #d1d5db", color: "#9ca3af", "& svg": { fontSize: 17 } }}>
        {icon}
        <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1 }}>{text}</Typography>
        <LockOutlined sx={{ fontSize: "14px !important" }} />
      </Stack>
    </Tooltip>
  );
}

function SortableRow({
  id,
  label,
  icon,
  audience,
  disabled,
  onUp,
  onDown,
}: {
  id: string;
  label: string;
  icon: ReactNode;
  audience: Audience;
  disabled: boolean;
  onUp?: () => void;
  onDown?: () => void;
}) {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const hidden = audience === "hidden";
  const tone = toneColor(theme, audience);
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, transition }}
      sx={{
        position: "relative",
        zIndex: isDragging ? 2 : 0,
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: 0.5,
        pr: 0.5,
        py: 0.75,
        borderRadius: 2,
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: isDragging ? "primary.main" : "#e5e7eb",
        boxShadow: isDragging ? "0 12px 28px rgba(17,24,39,.18)" : "0 1px 2px rgba(16,24,40,.05)",
        opacity: hidden ? 0.6 : 1,
      }}
    >
      <ButtonBase
        {...attributes}
        {...listeners}
        disabled={disabled}
        aria-label={`Drag ${label}`}
        sx={{ p: 0.5, borderRadius: 1, color: "#9ca3af", cursor: disabled ? "default" : isDragging ? "grabbing" : "grab", touchAction: "none" }}
      >
        <DragIndicator sx={{ fontSize: 18 }} />
      </ButtonBase>
      <Box sx={{ display: "flex", color: hidden ? "#9ca3af" : "#4b5563", "& svg": { fontSize: 18 } }}>{icon}</Box>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>{label}</Typography>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: tone, flexShrink: 0 }} aria-hidden />
      {!disabled && (
        <Stack direction="row">
          <IconButton size="small" aria-label={`Move ${label} up`} disabled={!onUp} onClick={onUp} sx={{ p: 0.25 }}>
            <ArrowUpward sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton size="small" aria-label={`Move ${label} down`} disabled={!onDown} onClick={onDown} sx={{ p: 0.25 }}>
            <ArrowDownward sx={{ fontSize: 15 }} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}
